import React from 'react';
import { cn } from '../../lib/cn';
import { Button } from '../Button/Button';

export type TiTabId = 'opportunity' | 'campaigns';

export interface TiTabsProps {
  active: TiTabId;
  onSelect?: ((tab: TiTabId) => void) | undefined;
}

/**
 * Treatment Intelligence tab bar — **two tabs** now: Opportunity, Campaigns.
 * The Call list tab moved to the new Calls section (spec 31 §7); a count strip
 * under the tabs links there.
 */
export function TiTabs({ active, onSelect }: TiTabsProps) {
  const tabs: { id: TiTabId; label: string }[] = [
    { id: 'opportunity', label: 'Opportunity' },
    { id: 'campaigns', label: 'Campaigns' },
  ];
  return (
    <div className="mt-4 flex items-center gap-1 border-b border-[var(--border-default)]">
      {tabs.map((t) => {
        const on = t.id === active;
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => onSelect?.(t.id)}
            className={cn(
              '-mb-px border-b-2 px-3.5 pb-2.5 pt-1 text-[14px]',
              on
                ? 'border-[var(--brand-indigo)] font-bold text-[var(--text-primary)]'
                : 'border-transparent font-medium text-[var(--text-muted)] hover:text-[var(--text-secondary)]',
            )}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

export interface TiHeaderProps {
  active: TiTabId;
  eyebrow: string;
  onSelectTab?: ((tab: TiTabId) => void) | undefined;
  onOpenSettings?: (() => void) | undefined;
  onNewCampaign?: (() => void) | undefined;
  /** Opens the Calls section filtered to Treatment (spec 31 §7). */
  onOpenCalls?: (() => void) | undefined;
}

/**
 * The shared header on all three TI tabs (§2): the same title and sub, Settings
 * and New campaign, then the tab bar. The eyebrow differs per tab.
 */
export function TiHeader({ active, eyebrow, onSelectTab, onOpenSettings, onNewCampaign, onOpenCalls }: TiHeaderProps) {
  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10.5px] font-bold uppercase tracking-[0.1em] text-[var(--text-muted)]">{eyebrow}</div>
          <h1 className="m-0 mt-1 text-[26px] font-bold leading-[1.15] tracking-[-0.025em] text-[var(--text-primary)]">
            Treatment <span className="text-[var(--brand-indigo)]">Intelligence</span>
          </h1>
          <p className="m-0 mt-1.5 max-w-[680px] text-[13px] leading-[1.6] text-[var(--text-muted)]">
            Diagnosed treatment that isn’t booked yet — what it’s worth, who to message, and who to call.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" iconLeft="cog" onClick={onOpenSettings}>
            Settings
          </Button>
          <Button variant="primary" size="sm" iconLeft="plus" onClick={onNewCampaign}>
            New campaign
          </Button>
        </div>
      </div>
      <TiTabs active={active} onSelect={onSelectTab} />
      {/* The calls-to-make strip (spec 31 §7) — the Call list tab's replacement. */}
      <div className="mt-3 flex flex-wrap items-center gap-2 rounded-[var(--r-lg)] border border-[color-mix(in_srgb,var(--warn-amber)_35%,transparent)] bg-[var(--warn-amber-fill)] px-4 py-2.5">
        <span className="text-[13px] font-bold text-[var(--warn-amber)]">9 to call</span>
        <span className="text-[12px] text-[var(--text-secondary)]">2 replied last night · 1 asked to be called</span>
        <button type="button" className="ml-auto text-[12.5px] font-semibold text-[var(--warn-amber)] hover:underline" onClick={onOpenCalls}>Open in Calls →</button>
      </div>
    </div>
  );
}
